'use client'

import React from 'react'
import type { AreaItem } from '@/types/cms/AreaItem'
import type { HistoryItem } from './NavigationViewDrawerTrigger.state'
import Icon from '@/services/components/strategies/common/Icon'
import Logo from '@/services/components/strategies/common/Logo/Logo'
import type { Context } from '@/types/app'
import ClientPortal from '@/services/components/strategies/common/ClientPortal/ClientPortal'
import type { ServerSection } from './NavigationViewDrawerTrigger.server'
import { cn } from '@/lib/ui/utils'

interface Props {
  nestedItems: AreaItem[]
  context: Context
  currentTitle?: string
  isOpen: boolean
  onClose?: () => void
  selectedIndex: number
  history: HistoryItem[]
  handleGoBack: () => void
  sections: ServerSection[] // ← all server-rendered levels
  currentKey: string // ← which to show
}

const NavigationViewDrawer: React.FC<Props> = ({
  currentTitle,
  isOpen,
  onClose,
  history,
  handleGoBack,
  sections,
  currentKey,
}) => {
  return (
    <ClientPortal>
      <div
        id="site-drawer"
        className={cn(
          'w-full h-full fixed inset-0 z-[1000] header-slide-over',
          isOpen ? 'visible' : 'invisible',
        )}
      >
        <button
          onClick={onClose}
          className={cn(
            'w-full h-full duration-500 ease-out transition-all inset-0 absolute bg-grey-400',
            isOpen ? 'opacity-70' : 'opacity-0',
          )}
          aria-label="Close menu overlay"
        />
        <div
          className={cn(
            'duration-300 transform ease-out transition-all absolute right-0 h-full w-full sm:w-96 bg-white px-4 overflow-y-auto',
            isOpen ? 'translate-x-0' : 'translate-x-full',
          )}
          role="dialog"
          aria-labelledby="nav-drawer-title"
        >
          <div className="relative h-20 mt-2 w-full px-[14px] flex flex-row items-center justify-between">
            {history?.length > 0 ? (
              <button
                onClick={handleGoBack}
                className="back-button"
                aria-label="Zurück"
              >
                <Icon name="arrow-left" size={20} className="text-gray-700" />
              </button>
            ) : (
              <span className="back-button relative">
                <Logo mobile="small" size="small" variant="dark" />
              </span>
            )}
            <button onClick={onClose} aria-label="Schließen">
              <Icon name="escape" size={24} className="text-gray-700" />
            </button>
          </div>

          {currentTitle ? (
            <h2 id="nav-drawer-title" className="px-4 text-2xl font-bold mt-2">
              {currentTitle}
            </h2>
          ) : (
            <span id="nav-drawer-title" className="sr-only">
              Navigation
            </span>
          )}

          <div className="px-4 pt-6">
            {sections.map((sec) => (
              <div key={sec.key} hidden={sec.key !== currentKey}>
                {sec.node}
              </div>
            ))}
          </div>
        </div>
      </div>
    </ClientPortal>
  )
}

export default NavigationViewDrawer
